import { r as register, x, u, k } from "./preact-core.js";
import { C as CustomSelect } from "./custom-select.js";
const _LinkedProducts = class _LinkedProducts extends x {
  constructor(props) {
    var _a;
    super(props);
    this.handleProductSelection = (data) => {
      if (data.value) {
        const product = data.value;
        this.setState({
          selectedProductLabel: product.label
        });
        if (product.url) {
          _LinkedProducts.navigateToProduct(product.url);
        }
      }
    };
    this.parsedLinkedProducts = JSON.parse(this.props.products);
    this.products = this.initializeProducts();
    this.state = {
      selectedProductLabel: ((_a = this.parsedLinkedProducts[0]) == null ? void 0 : _a.label) || _LinkedProducts.DEFAULT_LABEL
    };
  }
  /**
   * Initializes products through the complete processing pipeline
   */
  initializeProducts() {
    if (_LinkedProducts.DEBUG_DUPLICATES) {
      console.log(
        "LinkedProducts: Raw parsed data:",
        this.parsedLinkedProducts.map((p) => ({
          label: p.label,
          id: p.id,
          labelLength: p.label.length,
          charCodes: Array.from(p.label).map((c) => c.charCodeAt(0))
        }))
      );
    }
    const decoded = this.decodeHtmlEntities(this.parsedLinkedProducts);
    if (_LinkedProducts.DEBUG_DUPLICATES) {
      console.log(
        "LinkedProducts: After HTML decoding:",
        decoded.map((p) => {
          var _a;
          return {
            label: p.label,
            id: p.id,
            labelLength: p.label.length,
            changed: p.label !== ((_a = this.parsedLinkedProducts.find((orig) => orig.id === p.id)) == null ? void 0 : _a.label)
          };
        })
      );
    }
    const deduplicated = this.removeDuplicates(decoded);
    const sorted = _LinkedProducts.sortProducts(deduplicated);
    if (_LinkedProducts.DEBUG_DUPLICATES) {
      console.log(
        "LinkedProducts: Final processed products:",
        sorted.map((p) => ({ label: p.label, id: p.id }))
      );
    }
    return sorted;
  }
  componentDidMount() {
    const savedScrollPosition = sessionStorage.getItem(
      _LinkedProducts.STORAGE_KEY
    );
    if (savedScrollPosition) {
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
        sessionStorage.removeItem(_LinkedProducts.STORAGE_KEY);
      });
    }
  }
  static decodeHtmlEntity(text) {
    if (!text || typeof text !== "string") return text;
    const decoder = document.createElement("div");
    decoder.innerHTML = text;
    const decoded = decoder.textContent || decoder.innerText || text;
    return decoded.replace(/\u00A0/g, " ").replace(/\u2000-\u200F/g, " ").replace(/\u2028|\u2029/g, " ");
  }
  static navigateToProduct(url) {
    sessionStorage.setItem(
      _LinkedProducts.STORAGE_KEY,
      window.scrollY.toString()
    );
    window.location.href = url;
  }
  /**
   * Custom sort function for products: samples last, partial matches prioritize shorter labels,
   * numeric labels sorted numerically, fallback to alphabetical
   */
  static sortProducts(products) {
    if (products.length <= 1) return products;
    return products.sort((a, b) => {
      const labelA = a.label.trim().toLowerCase();
      const labelB = b.label.trim().toLowerCase();
      const isSampleA = /\bsample\b/.test(labelA);
      const isSampleB = /\bsample\b/.test(labelB);
      if (isSampleA && !isSampleB) return 1;
      if (isSampleB && !isSampleA) return -1;
      if (labelA.startsWith(labelB) && labelA !== labelB) return 1;
      if (labelB.startsWith(labelA) && labelA !== labelB) return -1;
      const numA = labelA.match(/^(\d+)/);
      const numB = labelB.match(/^(\d+)/);
      if (numA && numB) {
        return parseInt(numA[1] || numA[0], 10) - parseInt(numB[1] || numB[0], 10);
      } else if (numA) {
        return -1;
      } else if (numB) {
        return 1;
      } else {
        return labelA.localeCompare(labelB);
      }
    });
  }
  /**
   * Decodes HTML entity references in product data
   * @param products Array of product options
   * @returns Processed array with decoded HTML entities
   */
  decodeHtmlEntities(products) {
    return products.map((product) => ({
      ...product,
      label: product.label ? _LinkedProducts.decodeHtmlEntity(product.label) : product.label,
      value: product.value ? _LinkedProducts.decodeHtmlEntity(product.value) : product.value
    }));
  }
  /**
   * Normalizes a label for comparison by removing extra whitespace,
   * converting to lowercase, and handling Unicode normalization
   */
  static normalizeLabel(label) {
    return label.trim().toLowerCase().replace(/\s+/g, " ").replace(/\s*&\s*/g, " & ").replace(/\s+/g, " ").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }
  /**
   * Removes duplicate product options based on normalized label comparison
   */
  removeDuplicates(products) {
    if (products.length <= 1) return products;
    const seen = /* @__PURE__ */ new Map();
    const uniqueProducts = [];
    const duplicateGroups = [];
    for (const product of products) {
      const normalizedLabel = _LinkedProducts.normalizeLabel(product.label);
      if (!seen.has(normalizedLabel)) {
        seen.set(normalizedLabel, product);
        uniqueProducts.push(product);
      } else {
        const keptProduct = seen.get(normalizedLabel);
        let duplicateGroup = duplicateGroups.find(
          (g) => g.normalized === normalizedLabel
        );
        if (!duplicateGroup) {
          duplicateGroup = {
            normalized: normalizedLabel,
            kept: keptProduct,
            duplicates: []
          };
          duplicateGroups.push(duplicateGroup);
        }
        duplicateGroup.duplicates.push(product);
      }
    }
    if (_LinkedProducts.DEBUG_DUPLICATES) {
      if (duplicateGroups.length > 0) {
        console.log(
          "LinkedProducts: Duplicate groups detected:",
          duplicateGroups.map((group) => ({
            normalizedLabel: group.normalized,
            keptItem: { label: group.kept.label, id: group.kept.id },
            removedItems: group.duplicates.map((d) => ({
              label: d.label,
              id: d.id
            })),
            totalDuplicates: group.duplicates.length
          }))
        );
      }
      console.log(
        `LinkedProducts: Processed ${products.length} products, kept ${uniqueProducts.length} unique items`
      );
    }
    return uniqueProducts;
  }
  renderProductButton(product) {
    const isCurrentProduct = _LinkedProducts.normalizeLabel(product.label) === _LinkedProducts.normalizeLabel(
      _LinkedProducts.decodeHtmlEntity(this.state.selectedProductLabel)
    );
    return /* @__PURE__ */ u(
      "button",
      {
        class: `ease leading-50 transition-opacity duration-200 hover:opacity-100 ${isCurrentProduct ? "font-medium opacity-100" : "opacity-40"}`,
        disabled: isCurrentProduct,
        onClick: () => {
          if (product.url && !isCurrentProduct) {
            _LinkedProducts.navigateToProduct(product.url);
          }
        },
        children: product.label
      }
    );
  }
  renderButtonsView() {
    const allProducts = this.products;
    if (allProducts.length === 0) {
      return /* @__PURE__ */ u("div", { class: "linked-products-buttons", children: "No products available" });
    }
    return /* @__PURE__ */ u("div", { class: "linked-products-buttons flex w-full justify-between gap-x-20 gap-y-10", children: [
      allProducts.map((product) => this.renderProductButton(product)),
      allProducts.length === 2 && /* @__PURE__ */ u("span", { class: "spacer block min-w-[40px]" })
    ] });
  }
  renderSelectView() {
    const currentProduct = this.products.find((p) => p.isCurrent) || this.products[0];
    return /* @__PURE__ */ u("div", { class: "linked-products-select", children: /* @__PURE__ */ u(
      CustomSelect,
      {
        title: this.state.selectedProductLabel,
        options: this.products,
        default: currentProduct,
        direction: "down",
        onSelected: this.handleProductSelection
      }
    ) });
  }
  render() {
    return /* @__PURE__ */ u(k, { children: this.props.type === "buttons" ? this.renderButtonsView() : this.renderSelectView() });
  }
};
_LinkedProducts.STORAGE_KEY = "linkedProductsScrollPosition";
_LinkedProducts.DEFAULT_LABEL = "Select Product";
_LinkedProducts.DEBUG_DUPLICATES = false;
let LinkedProducts = _LinkedProducts;
function LinkedProducts$1() {
  register(LinkedProducts, "linked-products", ["products", "type"]);
}
LinkedProducts$1();
